import type { DateBound, DateUnit } from '@/components/tree-dnd/tree-dnd-types';
import {
  absoluteBound,
  relativeBound,
  todayIso,
} from '@/components/tree-dnd/tree-dnd-conditions/date-filter-utils';
import { DateInputPicker } from '@/components/ui/date-input-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18nContext } from '@/i18n/i18n-react';

interface DateBoundControlProps {
  value: DateBound;
  onChange: (value: DateBound) => void;
  /** When true, epoch is offered as a bound type (for custom windows). */
  allowEpoch?: boolean;
}

type BoundKind = 'relative' | 'absolute' | 'epoch';

function boundKind(value: DateBound): BoundKind {
  return value.type;
}

export function DateBoundControl({
  value,
  onChange,
  allowEpoch = false,
}: DateBoundControlProps) {
  const { LL } = useI18nContext();
  const kind = boundKind(value);

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={kind}
        onValueChange={(next) => {
          if (next === 'relative') {
            onChange(
              value.type === 'relative' ? value : relativeBound(7, 'days')
            );
            return;
          }
          if (next === 'absolute') {
            onChange(
              value.type === 'absolute' ? value : absoluteBound(todayIso())
            );
            return;
          }
          onChange({ type: 'epoch' });
        }}
      >
        <SelectTrigger className="h-8 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="relative">
            {LL.audiences.filters.date.boundRelative()}
          </SelectItem>
          <SelectItem value="absolute">
            {LL.audiences.filters.date.boundAbsolute()}
          </SelectItem>
          {allowEpoch || kind === 'epoch' ? (
            <SelectItem value="epoch">
              {LL.audiences.filters.date.boundEpoch()}
            </SelectItem>
          ) : null}
        </SelectContent>
      </Select>

      {value.type === 'relative' ? (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            className="h-8 w-20"
            value={value.offset}
            onChange={(event) => {
              const offset = Math.max(0, Number(event.target.value) || 0);
              onChange({ ...value, offset });
            }}
          />
          <Select
            value={value.unit}
            onValueChange={(unit) =>
              onChange({ ...value, unit: unit as DateUnit })
            }
          >
            <SelectTrigger className="h-8 w-auto min-w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="days">
                {LL.audiences.filters.date.days()}
              </SelectItem>
              <SelectItem value="weeks">
                {LL.audiences.filters.date.weeks()}
              </SelectItem>
              <SelectItem value="months">
                {LL.audiences.filters.date.months()}
              </SelectItem>
              <SelectItem value="years">
                {LL.audiences.filters.date.years()}
              </SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {LL.audiences.filters.date.beforeSend()}
          </span>
        </div>
      ) : null}

      {value.type === 'absolute' ? (
        <DateInputPicker
          value={value.date}
          placeholder={LL.audiences.filters.date.pickDate()}
          onChange={(date) => onChange({ ...value, date })}
        />
      ) : null}
    </div>
  );
}
